// src/receptionist/screens/Setup.tsx
//
// Everything a merchant sets up before the receptionist answers customers, in
// one menu (ourteam-frontend#4796), laid out as Oura's merchant To do → Setup:
// the open step on the left, "Your setup" with its progress on the right.
//
// The steps are the screens they always were (Business profile, the WhatsApp
// card, Staff, the Services sign-off, the template pack, Learn), drawn inside
// <Embedded>, so a step saves, signs off and submits exactly as before. Where
// a step stands is read from the same endpoints the old screens read: the
// WhatsApp checklist and the staff channel.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import {
    BadgeCheckIcon, Building2Icon, CheckIcon, ChevronRightIcon, FileTextIcon, ListChecksIcon,
    PhoneIcon, SparklesIcon, UsersRoundIcon, type LucideIcon,
} from "lucide-react";
import { FOCUS_RING, PillLink, ROW_HOVER, SURFACE, TYPE } from "@oura/ui";
import { cn } from "@oura/lib/cn";
import { getStaffChannel, getWhatsappState, type StaffChannelState, type WhatsappState } from "@service/receptionist";
import { Embedded, InfoNotice, PageHeader } from "../components/kit";
import { WhatsappOnboarding } from "../components/WhatsappOnboarding";
import { SETUP_CHANGED_EVENT } from "../components/SetupBanner";
import { MERCHANT_NAME_EVENT, MerchantGate } from "../merchantState";
import { BusinessProfileScreen } from "./BusinessProfile";
import { StaffScreen } from "./Staff";
import { KnowledgeSignOff } from "./Services";
import { LearnFromChatsScreen } from "./LearnFromChats";

export type SetupStepKey = "business" | "whatsapp" | "team" | "knowledge" | "templates" | "learn";

/** The Setup menu's steps, in order. Overview's Finish setup card reads the same list (#4847). */
export const SETUP_STEPS: { key: SetupStepKey; title: string; icon: LucideIcon; optional?: true }[] = [
    { key: "business", title: "Your business", icon: Building2Icon },
    { key: "whatsapp", title: "WhatsApp number", icon: PhoneIcon },
    { key: "team", title: "Your team", icon: UsersRoundIcon },
    { key: "knowledge", title: "Knowledge base", icon: BadgeCheckIcon },
    { key: "templates", title: "Message templates", icon: FileTextIcon },
    { key: "learn", title: "Learn from your chats", icon: SparklesIcon, optional: true },
];

const STEP_KEYS = SETUP_STEPS.map((s) => s.key);
export const isSetupStep = (v: string | null): v is SetupStepKey => !!v && (STEP_KEYS as string[]).includes(v);

type Status = { done: boolean; fact: string };

/** A step's state and the fact beside it, as the Setup menu shows them; null while unknown. Shared with Overview (#4847). */
export function statusOf(key: SetupStepKey, wa: WhatsappState | null, channel: StaffChannelState | null): Status | null {
    if (key === "learn") return { done: false, fact: "Optional" };
    if (key === "team") {
        if (!channel) return null;
        return channel.connected
            ? { done: true, fact: `${channel.memberCount} of ${channel.invitedCount} connected` }
            : { done: false, fact: `${channel.memberCount} of ${channel.invitedCount} connected` };
    }
    if (!wa) return null;
    const c = wa.checklist;
    switch (key) {
        case "business":
            return { done: c.detailsConfirmed, fact: c.detailsConfirmed ? "Done" : "Save your details" };
        case "whatsapp":
            return { done: c.numberMigrated, fact: wa.onPlatformNumber ? "On ourteam's number" : c.numberMigrated ? "Done" : "Not connected" };
        case "knowledge":
            return { done: c.kbSignedOff, fact: c.kbSignedOff ? "Signed off" : "Sign off" };
        case "templates": {
            const approved = wa.templates.filter((t) => t.status === "approved").length;
            return { done: c.templatesApproved, fact: `${approved} of ${wa.templates.length} approved` };
        }
    }
}

/** Oura's step mark: a green check when done, a blue ring on the open step, a grey ring otherwise. */
function Mark({ done, on }: { done: boolean; on: boolean }) {
    if (done) {
        return (
            <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-500 text-white">
                <CheckIcon size={12} strokeWidth={3} />
            </span>
        );
    }
    return (
        <span className={cn("grid h-5 w-5 place-items-center rounded-full border-2 bg-white", on ? "border-blue-600" : "border-gray-300")}>
            <span className={cn("h-2 w-2 rounded-full", on ? "bg-blue-600" : "bg-transparent")} />
        </span>
    );
}

export function SetupScreen({ merchantId, servicesHref, followUpsHref }: { merchantId: string | null; servicesHref: string; followUpsHref: string }) {
    const [params, setParams] = useSearchParams();
    const [wa, setWa] = useState<WhatsappState | null>(null);
    const [channel, setChannel] = useState<StaffChannelState | null>(null);

    // Reads overlap (mount, a step change, a save): only the latest one lands,
    // so a slow earlier read never puts back progress that has moved (Jack, #4797).
    const readSeq = useRef(0);
    const [loadError, setLoadError] = useState(false);
    const read = useCallback(async () => {
        if (!merchantId) return;
        const mine = ++readSeq.current;
        const [nextWa, nextChannel] = await Promise.allSettled([getWhatsappState(merchantId), getStaffChannel(merchantId)]);
        if (mine !== readSeq.current) return;
        if (nextWa.status === "fulfilled") setWa(nextWa.value);
        if (nextChannel.status === "fulfilled") setChannel(nextChannel.value);
        setLoadError(nextWa.status === "rejected" || nextChannel.status === "rejected");
    }, [merchantId]);
    useEffect(() => {
        void read();
        return () => { readSeq.current += 1; };
    }, [read]);
    // A save on a step (details, people) is announced; read where things stand again.
    useEffect(() => {
        window.addEventListener(MERCHANT_NAME_EVENT, read);
        window.addEventListener(SETUP_CHANGED_EVENT, read);
        return () => {
            window.removeEventListener(MERCHANT_NAME_EVENT, read);
            window.removeEventListener(SETUP_CHANGED_EVENT, read);
        };
    }, [read]);

    const statuses = useMemo(
        () => Object.fromEntries(SETUP_STEPS.map((s) => [s.key, statusOf(s.key, wa, channel)])) as Record<SetupStepKey, Status | null>,
        [wa, channel],
    );
    const required = SETUP_STEPS.filter((s) => !s.optional);
    const known = required.every((s) => statuses[s.key] !== null);
    const done = required.filter((s) => statuses[s.key]?.done).length;
    const firstOpen = required.find((s) => !statuses[s.key]?.done)?.key ?? "learn";

    const asked = params.get("step");
    const current: SetupStepKey = isSetupStep(asked) ? asked : known ? firstOpen : "business";
    const go = (key: SetupStepKey) => {
        const next = new URLSearchParams(params);
        next.set("step", key);
        setParams(next, { replace: false });
        void read();
    };
    const step = SETUP_STEPS.find((s) => s.key === current)!;
    const index = SETUP_STEPS.indexOf(step);
    const pct = required.length ? Math.round((done / required.length) * 100) : 0;

    let body: ReactNode = null;
    if (!merchantId) body = null;
    else if (current === "business") {
        body = <MerchantGate merchantId={merchantId}>{(p) => <BusinessProfileScreen {...p} parts="business" />}</MerchantGate>;
    } else if (current === "whatsapp") {
        // The merchant's own typed number, so the card says when it is not the one the receptionist answers on.
        body = (
            <MerchantGate merchantId={merchantId}>
                {(p) => <WhatsappOnboarding merchantId={merchantId} typedNumber={p.merchant.whatsappNumber} show="number" />}
            </MerchantGate>
        );
    } else if (current === "team") {
        body = (
            <div className="space-y-4">
                {/* What finishes this step, in so many words: it was not on the page (29 Oct 2026). */}
                <div className={cn(SURFACE.card, "p-5")}>
                    <p className={cn(TYPE.tb, "m-0 font-medium text-gray-900")}>
                        {channel?.connected ? "Your team is connected." : "Done once someone on your team has messaged the receptionist's number."}
                    </p>
                    <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>
                        WhatsApp has no group API, so booking requests and alerts reach each person on their own WhatsApp. Messaging the number
                        once proves it reaches them.
                    </p>
                    <ol className={cn(TYPE.tc, "m-0 mt-3 list-decimal space-y-1 pl-5 text-gray-700")}>
                        <li>Add each person under <span className="font-medium">People</span> with their WhatsApp number, then Save changes.</li>
                        <li>Press <span className="font-medium">Send link</span> beside their name, or forward the link in the Staff channel card.</li>
                        <li>They press Send in WhatsApp. Their row reads Reachable, and this step ticks.</li>
                    </ol>
                    {channel && (
                        <p className={cn(TYPE.tc, "m-0 mt-3 font-medium tabular-nums text-gray-700")}>
                            {channel.memberCount} of {channel.invitedCount} {channel.invitedCount === 1 ? "person" : "people"} connected
                        </p>
                    )}
                </div>
                <MerchantGate merchantId={merchantId}>{(p) => <StaffScreen {...p} />}</MerchantGate>
            </div>
        );
    } else if (current === "knowledge") {
        body = (
            <div className="space-y-4">
                <KnowledgeSignOff key={merchantId} merchantId={merchantId} dirty={false} />
                <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                    Services, prices, policies and FAQ notes are edited on Services. Sign off once they are right.
                </p>
                <PillLink to={servicesHref} icon={ListChecksIcon}>Open Services</PillLink>
            </div>
        );
    } else if (current === "templates") {
        body = (
            <div className="space-y-4">
                <InfoNotice>
                    Each template is a follow-up's wording, written in Conversation → Follow-ups. Edit it there, then submit it here for
                    Meta's approval. Meta is shown an example for every detail the message fills in.
                </InfoNotice>
                <WhatsappOnboarding merchantId={merchantId} typedNumber="" show="templates" followUpsHref={followUpsHref} />
            </div>
        );
    } else {
        body = <LearnFromChatsScreen merchantId={merchantId} />;
    }

    return (
        <div>
            <PageHeader
                title="Setup"
                subtitle="A few things to set up before the receptionist answers your customers."
                status={known ? `${done} of ${required.length} done` : undefined}
            />
            <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
                <section className="min-w-0 space-y-4">
                    <header className={cn(SURFACE.card, "flex items-center gap-3 px-5 py-4")}>
                        <step.icon aria-hidden size={20} strokeWidth={2} className="shrink-0 text-blue-600" />
                        <h2 className={cn(TYPE.tt, "m-0 min-w-0 flex-1 text-gray-900")}>{step.title}</h2>
                        <span className={cn(TYPE.tc, "shrink-0 tabular-nums text-gray-500")}>
                            {step.optional ? "Optional" : `Step ${index + 1} of ${required.length}`}
                        </span>
                    </header>
                    <Embedded key={current}>{body}</Embedded>
                </section>

                <aside className={cn(SURFACE.card, "p-5 lg:sticky lg:top-6")}>
                    <h2 className="m-0 mb-3.5 flex items-center gap-2.5 text-gray-900">
                        <ListChecksIcon aria-hidden size={20} strokeWidth={2} className="shrink-0 text-blue-600" />
                        <span className={TYPE.tt}>Your setup</span>
                    </h2>
                    {loadError && (
                        <div role="alert" className={cn(TYPE.tc, "mb-3 flex items-center justify-between gap-3 rounded-xl bg-rose-50 px-3 py-2 text-rose-700 ring-1 ring-inset ring-rose-200")}>
                            <span>Could not read where setup stands.</span>
                            <button type="button" onClick={() => void read()} className={cn("rounded font-medium underline underline-offset-2", FOCUS_RING)}>Try again</button>
                        </div>
                    )}
                    <nav aria-label="Setup steps">
                        <div className="mb-2 flex items-baseline justify-between gap-3">
                            <span className={cn(TYPE.tc, "text-gray-500")}>
                                {!known ? "Reading where things stand…" : done === required.length ? "Everything is set up." : `${required.length - done} ${required.length - done === 1 ? "step" : "steps"} left.`}
                            </span>
                            {known && (
                                <span className={cn(TYPE.tm, "tabular-nums", done === required.length ? "font-medium text-emerald-600" : "text-gray-400")}>
                                    {done} of {required.length}
                                </span>
                            )}
                        </div>
                        <div role="progressbar" aria-label="Setup" aria-valuemin={0} aria-valuemax={required.length} aria-valuenow={done} className="mb-2 h-1.5 overflow-hidden rounded-full bg-gray-100">
                            <div className="h-full rounded-full bg-blue-600 transition-[width] duration-500 motion-reduce:transition-none" style={{ width: `${pct}%` }} />
                        </div>
                        <ol className="list-plain m-0 divide-y divide-gray-100 p-0">
                            {SETUP_STEPS.map((s) => {
                                const on = s.key === current;
                                const st = statuses[s.key];
                                return (
                                    <li key={s.key}>
                                        <button
                                            type="button"
                                            onClick={() => go(s.key)}
                                            aria-current={on ? "step" : undefined}
                                            className={cn("flex min-h-11 w-full items-center gap-3 rounded-md text-left", ROW_HOVER, FOCUS_RING)}
                                        >
                                            <span aria-hidden className="flex w-5 shrink-0 justify-center"><Mark done={!!st?.done} on={on} /></span>
                                            <span className={cn(TYPE.tb, "min-w-0 flex-1 sm:truncate", on ? "font-medium text-blue-600" : "text-gray-900 group-hover:text-blue-600")}>
                                                {s.title}
                                                <span className="sr-only">, {st?.done ? "done" : s.optional ? "optional" : "not done"}</span>
                                            </span>
                                            <span className={cn(TYPE.tc, "shrink-0 whitespace-nowrap tabular-nums text-gray-500")}>{st?.fact ?? ""}</span>
                                            <ChevronRightIcon aria-hidden size={14} className="shrink-0 text-gray-300 group-hover:text-gray-500" />
                                        </button>
                                    </li>
                                );
                            })}
                        </ol>
                    </nav>
                </aside>
            </div>
        </div>
    );
}
